import * as SVG from 'svg.js';

import { Utils } from 'app/core/utils';
import { StageConstants } from 'core/constants';
import { extractChunksFromLine, FontBearingDescription, FontDescription,
         LeadingFactor, normalizedFontFamily, normalizedTextChunk,
         scaledBearing, Text, TextAlignment, TextArea, TextChunk, TextChunkKind,
         TextColor, TextSize,
       } from './text.model';

export function applyModel(
  wrapper: SVG.Doc, model: Text, font: FontDescription): SVG.Doc
{
  const txt = wrapper.select('text').first() as SVG.Text;

  applyChunksToNode(txt, model.text);
  applyFontFamily(txt, model.fontName);
  applyFontSize(txt, model.fontSize);
  applyColor(txt, model.color);
  applyLeading(txt, model.leading);

  applyHorizontalAlignment(
    wrapper, txt, model.horizontalAlignment, scaledBearing(font, model));

  applyVerticalAlignment(
    wrapper, txt, model.verticalAlignment, model.fontSize, model.leading);

  return wrapper;
}

export function extractModel(wrapper: SVG.Doc): Text
{
  const txt = wrapper.select('text').first() as SVG.Text;

  return {
    text: extractChunksFromNode(txt),
    horizontalAlignment: extractHorizontalAlignment(txt),
    verticalAlignment: extractVerticalAlignment(txt),
    fontSize: extractFontSize(txt),
    fontName: extractFontFamily(txt),
    color: extractFontColor(txt),
    leading: extractLeading(txt),
    availableArea: extractAvailableArea(wrapper)
  };
}

interface AlignmentAttributeMappingPair {
  attribute: string;
  alignment: TextAlignment;
}

interface AlignmentAttributeMapping {
  attributeName: string;
  values: Array<AlignmentAttributeMappingPair>;
}

const VERTICAL_ALIGNMENT_MAPPING: AlignmentAttributeMapping = {
  attributeName: StageConstants.ATTRIBUTE_VERTICAL_ALIGNMENT,
  values: [
    { attribute: 'start', alignment: TextAlignment.Start },
    { attribute: 'middle', alignment: TextAlignment.Middle },
    { attribute: 'end', alignment: TextAlignment.End },
  ]
};

const HORIZONTAL_ALIGNMENT_MAPPING: AlignmentAttributeMapping = {
  attributeName: 'text-anchor',
  values: [
    { attribute: 'start', alignment: TextAlignment.Start },
    { attribute: 'middle', alignment: TextAlignment.Middle },
    { attribute: 'end', alignment: TextAlignment.End },
  ]
};

const DEFAULT_ALIGNMENT = TextAlignment.Start;
const DEFAULT_FONT_FAMILY = 'Open Sans';
const DEFAULT_FONT_SIZE = '16';
const DEFAULT_FONT_COLOR = '#000000';
const DEFAULT_LEADING = 1.3;

function lookupAlignmentByValue(
  mapping: AlignmentAttributeMapping, attribute: string): TextAlignment
{
  const entry = Utils.find(
    mapping.values,
    (el: AlignmentAttributeMappingPair) => el.attribute === attribute);

  return entry ? entry.alignment : DEFAULT_ALIGNMENT;
}

function lookupAlignment(
  mapping: AlignmentAttributeMapping, node: SVG.Text): TextAlignment
{
  return lookupAlignmentByValue(mapping, node.attr(mapping.attributeName));
}

function lookupAttributeValue(
  mapping: AlignmentAttributeMapping, alignment: TextAlignment): string
{
  return Utils.find(
    mapping.values,
    (el: AlignmentAttributeMappingPair) => el.alignment === alignment
  ).attribute;
}

function readStyleFromFirstSpan(
  node: SVG.Text, styleAttribute: string, defaultValue: string): string
{
  const firstSpan = node.lines().first();
  const value = firstSpan
    ? firstSpan.style(styleAttribute).toString()
    : undefined;

  return (value !== undefined && value !== '') ? value : defaultValue;
}

function getFirstLineOffset(node: SVG.Text): TextSize
{
  const firstSpan = node.lines().first();
  return parseInt(firstSpan ? firstSpan.attr('dy') : undefined, 10);
}

function extractAvailableArea(wrapper: SVG.Doc): TextArea
{
  return {
    width: wrapper.width(),
    height: wrapper.height(),
  };
}

function extractLeading(node: SVG.Text): LeadingFactor
{
  const fontSize = extractFontSize(node);
  const dy = getFirstLineOffset(node);

  return dy ? (dy / fontSize) : DEFAULT_LEADING;
}

function extractFontColor(node: SVG.Text): TextColor
{
  return readStyleFromFirstSpan(node, 'fill', DEFAULT_FONT_COLOR);
}

function extractFontFamily(node: SVG.Text): string
{
  return normalizedFontFamily(
    readStyleFromFirstSpan(node, 'font-family', DEFAULT_FONT_FAMILY));
}

function extractFontSize(node: SVG.Text): TextSize
{
  return parseInt(
    readStyleFromFirstSpan(node, 'font-size', DEFAULT_FONT_SIZE), 10);
}

function extractHorizontalAlignment(node: SVG.Text): TextAlignment
{
  return lookupAlignment(HORIZONTAL_ALIGNMENT_MAPPING, node);
}

function extractVerticalAlignment(node: SVG.Text): TextAlignment
{
  return lookupAlignment(VERTICAL_ALIGNMENT_MAPPING, node);
}

export function extractChunksFromNode(node: SVG.Text): Array<TextChunk>
{
  const lines = (<any>node.lines())
    .members
    .map((m: SVG.Tspan) => m.text())
    .map(extractChunksFromLine);

  const newline = {
    kind: TextChunkKind.Whitespace,
    breaksLine: true,
    content: '',
  };

  return Utils
    .flattenArray(Utils.intersperse(lines, [newline]))
    .map(normalizedTextChunk);
}

function applyChunksToNode(node: SVG.Text, chunks: Array<TextChunk>): void
{
  const renderWord = (word: TextChunk) => word.content;
  const renderWhitespace = (space: TextChunk) => space.breaksLine ? '\n' : ' ';

  const renderChunk = (chunk: TextChunk): string => {
    switch (chunk.kind) {
      case TextChunkKind.Word: return renderWord(chunk) || '';
      case TextChunkKind.Whitespace: return renderWhitespace(chunk);
      case TextChunkKind.Control: return '';
      default: return ''; // not reached
    }
  };

  node.text(chunks.map(renderChunk).join(''));
}

function applyHorizontalAlignment(
  wrapper: SVG.Doc,
  node: SVG.Text,
  alignment: TextAlignment,
  bearing: FontBearingDescription): void
{
  node.attr(HORIZONTAL_ALIGNMENT_MAPPING.attributeName,
            lookupAttributeValue(HORIZONTAL_ALIGNMENT_MAPPING, alignment));

  const width = wrapper.width();

  const svgAlignment = (() => {
    switch (alignment) {
      case TextAlignment.Start: return bearing.left;
      case TextAlignment.Middle: return width / 2;
      case TextAlignment.End: return width - bearing.right;
      default: return 0; // not reached
    }
  })();

  node.transform({x: 0});
  node.attr('x', svgAlignment);
}

function applyVerticalAlignment(
  wrapper: SVG.Doc,
  node: SVG.Text,
  alignment: TextAlignment,
  fontSize: TextSize,
  leading: LeadingFactor): void
{
  node.attr(VERTICAL_ALIGNMENT_MAPPING.attributeName,
            lookupAttributeValue(VERTICAL_ALIGNMENT_MAPPING, alignment));

  const height = wrapper.height();
  const lineHeight = fontSize * leading;
  const textHeight = (<any>node.lines()).members.length * lineHeight;
  const additionalLeading = lineHeight - fontSize;

  const svgAlignment = (() => {
    switch (alignment) {
      case TextAlignment.Start: return {
        baseLine: 0,
        offset: 0,
      };
      case TextAlignment.Middle: return {
        baseLine: (height / 2),
        offset: -(textHeight / 2),
      };
      case TextAlignment.End: return {
        baseLine: height,
        offset: -textHeight,
      };
      default: return { // not reached
        baseLine: 0,
        offset: 0,
      };
    }
  })();

  node.attr(
    'y', svgAlignment.baseLine + svgAlignment.offset - additionalLeading);
}

function applyStyleToLines(node: SVG.Text, style: string, value: string): void
{
  for (const line of (<any>node.lines()).members) {
    line.style(style, value);
  }
}

function applyFontSize(node: SVG.Text, size: TextSize): void
{
  applyStyleToLines(node, 'font-size', `${size}px`);
  node.font({'size': size});
}

function applyFontFamily(node: SVG.Text, family: string): void
{
  applyStyleToLines(node, 'font-family', `${family}`);
  node.font({'family': family});
}

function applyColor(node: SVG.Text, color: TextColor): void
{
  applyStyleToLines(node, 'fill', color);
}

function applyLeading(node: SVG.Text, leading: LeadingFactor): void
{
  node.leading(leading);
}



// WEBPACK FOOTER //
// ./src/app/text/text.view.svg.ts